import { Box, Paper } from "@mui/material";
import { WidgetGrid } from "./widget-grid";
import { OutlinedSelect } from "../../components/outlined-select";
import { WidgetType } from "../../utils/asset-dashboard/types";
import {
  ActionTypes,
  useWidgetContext,
  useWidgetContextDispatch,
} from "../../hooks/use-widget-context";
import { ViewContainer } from "../../components/view-container";
import { styleConstants } from "../../utils/colors";
import { useMemo, useState } from "react";
import { useCapabilities } from "@hooks/use-capabilities";

const labels = {
  [WidgetType.ASSET_TABLE]: "Asset Table",
  [WidgetType.ASSET_CHART]: "Asset Chart",
  [WidgetType.TICKET_TABLE]: "Ticket Table",
  [WidgetType.TICKET_CHART]: "Ticket Chart",
  [WidgetType.USER_TABLE]: "Users Table",
  [WidgetType.USER_CHART]: "Users Chart",
};

const widgetCapabilities: Record<WidgetType, string> = {
  [WidgetType.ASSET_TABLE]: "read_asset",
  [WidgetType.ASSET_CHART]: "read_asset",
  [WidgetType.TICKET_TABLE]: "read_ticket",
  [WidgetType.TICKET_CHART]: "read_ticket",
  [WidgetType.USER_TABLE]: "list_users",
  [WidgetType.USER_CHART]: "list_users",
};

export const Dashboard = () => {
  const dispatch = useWidgetContextDispatch();
  const { widgets } = useWidgetContext();
  const { can } = useCapabilities();
  const [selectedWidget, setSelectedWidget] = useState<WidgetType | "">("");

  const widgetOptions = useMemo(
    () =>
      Object.values(WidgetType)
        .filter((widget) => can(widgetCapabilities[widget]))
        .map((widget) => ({
          label: labels[widget],
          value: widget,
        })),
    [can]
  );

  const handleNewWidget = (widget: WidgetType | "") => {
    if (!widget) return;
    if (!can(widgetCapabilities[widget])) {
      setSelectedWidget("");
      return;
    }

    const isTableWidget =
      widget === WidgetType.ASSET_TABLE ||
      widget === WidgetType.TICKET_TABLE ||
      widget === WidgetType.USER_TABLE;

    dispatch({
      type: ActionTypes.ADD_WIDGET,
      payload: {
        id: widget + Date.now(),
        type: widget,
        position: {
          width: isTableWidget ? 4 : 1,
          height: 1,
          index: widgets.length,
        },
        config: {
          title: labels[widget],
        },
      },
    });
    setSelectedWidget("");
  };

  return (
    <ViewContainer
      className="asset-dashboard-view"
      sx={{
        minHeight: "100vh",
        p: 1,
        pb: { xs: "calc(61px + env(safe-area-inset-bottom))", md: 1 },
      }}
    >
      <Box>
        <Paper
          elevation={1}
          sx={{
            p: 1,
            position: "sticky",
            top: { xs: styleConstants.mobileSidebarHeight, md: 0 },
            zIndex: (theme) => theme.zIndex.appBar - 1,
          }}
        >
          <OutlinedSelect<WidgetType | "">
            label="New Widget"
            value={selectedWidget}
            options={widgetOptions}
            onChange={handleNewWidget}
            wrapperSx={{
              maxWidth: "280px",
            }}
          />
        </Paper>
        <Box>
          <WidgetGrid />
        </Box>
      </Box>
    </ViewContainer>
  );
};
